import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import { useShop } from "@/hooks/use-shop.ts";
import { isRunningReason, Machine } from "@/hooks/use-shop-details.ts";
import { Card } from "@/components/ui/card.tsx";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import { cn, formatCurrency } from "@/lib/utils.ts";
import { z } from "zod";
import { useMuiModal } from "@/components/modal.tsx";
import { sortBy } from "lodash-es";
import { MachineSelectorModal } from "@/components/machine-selector-modal.tsx";
import { ClosingWarningModal } from "@/components/closing-warning-modal";
import { LoadingSpinner } from "@/components/spinner";
import { ReactNode } from "react";
import { useCoupon } from "@/hooks/use-coupon";
import { useShopDetails } from "@frontend-common/hooks/use-shop-details";
import { getFeatureFlags } from "@/lib/runtime-config";
import { recordTrackedEvent } from "@/lib/kiosk-session-tracking";
import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { MachineStatusIcon } from "@frontend-common/icons/machine-status-icon";
import { FurFriendlyIcon } from "@frontend-common/icons/fur-friendly-icon";
import {
  getMachineIconCapacityScale,
  MAX_MACHINE_ICON_CAPACITY_SCALE,
} from "@frontend-common/icons/machine-status-icon.shared";
import type { MachineRunFinishWindow } from "@frontend-common/runs/machine-run-progress";
import { useKioskNthPurchaseCampaignHold } from "@/features/nth-purchase-campaign/nth-purchase-campaign-banner";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import {
  machineAccent,
  type MachineAccent,
} from "@/features/payment-flow/flow-header";
const WARNING_MINUTES = 40;

function isNearClosingForWasher(details: {
  closing: string;
  lastWasherStart: string;
}): boolean {
  const closing = details.closing || "00:00";
  const lastWasherStart = details.lastWasherStart || "00:00";

  // "00:00" means no restriction
  const deadlines = [closing, lastWasherStart].filter((t) => t !== "00:00");
  if (deadlines.length === 0) return false;

  const nowMinutes = getCurrentTimeMinutes();
  return deadlines.some((deadline) => {
    const deadlineMinutes = parseTimeToMinutes(deadline);
    const minutesUntilDeadline = deadlineMinutes - nowMinutes;
    return minutesUntilDeadline >= 0 && minutesUntilDeadline <= WARNING_MINUTES;
  });
}

function parseTimeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
}

function getCurrentTimeMinutes(): number {
  // Use Budapest timezone to match the shop availability check
  const now = new TZDate(new Date(), "Europe/Budapest");
  return parseInt(format(now, "HH")) * 60 + parseInt(format(now, "mm"));
}

export const Route = createFileRoute("/machine-selector-screen/$machine-type")({
  component: RouteComponent,
  params: {
    parse: (params) =>
      z
        .object({
          "machine-type": z.enum(["drying-machines", "washing-machines"]),
        })
        .parse(params),
  },
  beforeLoad: ({ params }) => {
    // the one-screen payment flow replaces this screen and the ones after it
    if (getFeatureFlags().washStartGuide) {
      throw redirect({
        to: "/payment/$machineType",
        params: { machineType: params["machine-type"] },
      });
    }
    sessionStorage.setItem("machine-type", params["machine-type"]);
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const params = Route.useParams();
  useAudioSlot(
    params["machine-type"] === "drying-machines"
      ? "page.machine-selector.drying"
      : "page.machine-selector.washing",
  );

  return (
    <Page className={`bg-background`}>
      <Header backArrowHref={`/machine-type-selector-screen`} />
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center`}>
          {params["machine-type"] === "drying-machines"
            ? t("Válaszd ki a szárítógépet!")
            : t("Válaszd ki a mosógépet!")}
        </h2>
        <MachineGrid machineType={params["machine-type"]} />
      </PageContent>
      <Footer helpText={<MachineSelectHelpText />} />
    </Page>
  );
}

/** The shop's washers or dryers, refreshed every few seconds. Shared by this
 * screen and the payment flow. */
export function MachineGrid({
  machineType,
  onSelect,
  hiddenMachineId,
  accentColors,
}: {
  machineType: "washing-machines" | "drying-machines";
  onSelect?: (machine: Machine, card: HTMLElement) => void;
  hiddenMachineId?: number;
  /** Dryers red, pet machines purple, like in the payment flow's header. */
  accentColors?: boolean;
}) {
  const { data: shop } = useShop();
  const coupon = useCoupon();
  const {
    data: details,
    isLoading,
    isMachineStatesPending,
  } = useShopDetails(shop?.shopId ?? null, {
    refetchInterval: 4000,
    couponNonceKey: coupon?.couponKey,
  });
  const { hold } = useKioskNthPurchaseCampaignHold(shop?.shopId ?? null);
  const machines = sortBy(
    details?.machines[
      machineType === "drying-machines" ? "dryers" : "washingMachines"
    ] ?? [],
    (o: Machine) => o.storePlace,
  );

  return (
    <div className={`flex flex-wrap gap-6 justify-center mt-8`}>
      {isLoading && <LoadingSpinner />}
      {isMachineStatesPending
        ? machines.map((_, i) => <MachineCardSkeleton key={i} />)
        : machines.map((machine, i) => (
            <MachineCard
              machine={machine}
              shopDetails={details}
              nthPurchaseCampaignPercentage={hold?.percentage}
              onSelect={onSelect}
              hidden={machine.id === hiddenMachineId}
              accentColors={accentColors}
              key={i}
            />
          ))}
    </div>
  );
}

export function MachineSelectHelpText() {
  return (
    <Trans>
      Nézd meg melyik gépbe rámoltál be, majd a gép tetején látható számot
      használd a gép kiválasztásánál.
    </Trans>
  );
}

const finishWindowLabels = {
  in_30_40_minutes: "Befejeződik 30-40 percen belül",
  in_20_30_minutes: "Befejeződik 20-30 percen belül",
  in_10_20_minutes: "Befejeződik 10-20 percen belül",
  in_5_10_minutes: "Befejeződik 5-10 percen belül",
} as const;

export function MachineCard({
  machine,
  shopDetails,
  nthPurchaseCampaignPercentage,
  onSelect,
  hidden,
  accentColors,
}: {
  machine: Machine;
  shopDetails?: { closing: string; lastWasherStart: string } | undefined;
  /** Set while the user holds an nth-purchase-campaign discount; the prices already
   * include it, this only shows it. */
  nthPurchaseCampaignPercentage?: number;
  /** Instead of the "Fontos!" modal and the next screen (the payment flow
   * does its own); gets the card, which it flies into its header. */
  onSelect?: (machine: Machine, card: HTMLElement) => void;
  /** Keeps its place in the grid while invisible (it flew away). */
  hidden?: boolean;
  /** Dryers red, pet machines purple, like in the payment flow's header. */
  accentColors?: boolean;
}) {
  const navigate = useNavigate();
  const { showModal } = useMuiModal();
  const { t } = useTranslation();
  const { data: shop } = useShop();
  const coupon = useCoupon();
  const isRunning =
    machine.state === "NOT_AVAILABLE" && isRunningReason(machine);
  const stateLabels = {
    AVAILABLE: t("Szabad"),
    AVAILABLE_BY_RESERVATION: t("Neked foglalva"),
    OUT_OF_ORDER: t("Üzemen kívül"),
    NOT_AVAILABLE: isRunning ? t("Használatban") : t("Nem elérhető"),
    RESERVED: t("Foglalt"),
  } as const;
  const formattedState = stateLabels[machine.state as keyof typeof stateLabels];
  const campaignOriginalPrice = nthPurchaseCampaignPercentage
    ? getOriginalPrice(machine)
    : null;
  const machineFinishWindow = (
    machine as { estimatedFinishWindow?: MachineRunFinishWindow }
  ).estimatedFinishWindow;
  const accent = accentColors ? cardAccent(machine) : null;

  return (
    <Card
      className={cn(
        "px-2 pb-2 pt-0 gap-0 rounded-lg shadow-xl border-2 min-w-[20vw]",
        hidden && "invisible",
        {
          "border-bub-vivid-blue":
            isAvailable(machine.state) &&
            !machine.furFriendly &&
            !machine.vegan,
          "border-gray-300": !isAvailable(machine.state),
          "border-bub-orange":
            machine.furFriendly && isAvailable(machine.state),
          "border-bub-vegan": machine.vegan && isAvailable(machine.state),
        },
        accent && ACCENT_CARD[accent].border,
      )}
      onClick={(event) => {
        const card = event.currentTarget;
        if (isAvailable(machine.state)) {
          const showMachineModal = () => {
            showModal(
              <MachineSelectorModal
                machineId={machine.id}
                machineType={machine.machineType}
                onAccept={() => {
                  void recordTrackedEvent({
                    eventType: "machine_selected",
                    machineId: machine.id,
                    payload: {
                      machineType: machine.machineType,
                      storePlace: machine.storePlace,
                    },
                  });
                  sessionStorage.setItem(
                    "machine-type",
                    machine.machineType === "mosogep"
                      ? "washing-machines"
                      : "drying-machines",
                  );
                  if (
                    machine.machineType === "szaritogep" &&
                    machine.dryerBundles?.length &&
                    machine.state !== "AVAILABLE_BY_RESERVATION"
                  ) {
                    navigate({
                      to: "/dryer-bundle-selector-screen/$machineId",
                      params: { machineId: machine.id },
                    });
                  } else {
                    navigate({
                      to: "/payment-selector-screen/$machineId/{-$dryerBundleId}",
                      params: { machineId: machine.id },
                    });
                  }
                }}
              />,
            );
          };

          const select = onSelect
            ? () => onSelect(machine, card)
            : showMachineModal;
          if (
            machine.machineType === "mosogep" &&
            shopDetails &&
            isNearClosingForWasher(shopDetails)
          ) {
            showModal(<ClosingWarningModal onContinue={select} />);
          } else {
            select();
          }
        }
      }}
    >
      <div className="flex justify-center gap-2 pb-2">
        <TopBadge
          className={cn(
            {
              "bg-bub-vivid-blue":
                isAvailable(machine.state) &&
                !machine.furFriendly &&
                !machine.vegan,
              "bg-gray-300": !isAvailable(machine.state),
              "bg-bub-orange":
                machine.furFriendly && isAvailable(machine.state),
              "bg-bub-vegan": machine.vegan && isAvailable(machine.state),
            },
            accent && ACCENT_CARD[accent].bg,
          )}
        >
          {machine.storePlace}
        </TopBadge>
        {nthPurchaseCampaignPercentage ? (
          campaignOriginalPrice !== null && (
            <TopBadge
              className={cn(
                isAvailable(machine.state) ? "bg-[#eb2d31]" : "bg-gray-300",
              )}
            >
              −{nthPurchaseCampaignPercentage}%
            </TopBadge>
          )
        ) : (
          machine.canUseCoupon && (
            <TopBadge className="bg-[#eb2d31]">%</TopBadge>
          )
        )}
      </div>
      <div className={`text-center font-bold text-[24px] min-h-8`}>
        {machine.furFriendly && (
          <small
            className={cn(
              `text-bub-orange block`,
              { "text-gray-300": !isAvailable(machine.state) },
              accent && ACCENT_CARD[accent].text,
            )}
          >
            {t("Állatbarát gép")}
          </small>
        )}
        {machine.vegan && (
          <small
            className={cn(`text-bub-vegan block`, {
              "text-gray-300": !isAvailable(machine.state),
            })}
          >
            {t("Vegán")}
          </small>
        )}
      </div>
      <span className="relative max-w-22 mx-auto w-full mb-1 h-[103px] flex items-end justify-center">
        {getFeatureFlags().machineStatusIcons === "new" ? (
          <MachineStatusIcon
            machineType={machine.machineType}
            status={
              machine.state === "NOT_AVAILABLE" && !isRunning
                ? "UNAVAILABLE"
                : machine.state
            }
            style={{
              height: `${(getMachineIconCapacityScale(machine.capacityType) / MAX_MACHINE_ICON_CAPACITY_SCALE) * 100}%`,
            }}
            title={`${machine.storePlace}. ${formattedState}`}
            // the dryer's icon keeps its usual colors
            color={accent === "pet" ? ACCENT_CARD.pet.color : undefined}
          />
        ) : (
          <img
            src={getLegacyMachineIcon(machine)}
            className="w-full h-full object-contain"
          />
        )}
        {/* the icon viewBox is centered on the cabinet, so anchor from the
            center to land on the drum's lower-right regardless of variant */}
        {machine.furFriendly && (
          <span className="absolute bottom-1 left-1/2 translate-x-[12px] flex rounded-full bg-white p-[3px] shadow-sm">
            <FurFriendlyIcon
              width={18}
              height={18}
              color={accent ? ACCENT_CARD[accent].color : "#f45e22"}
            />
          </span>
        )}
      </span>
      <small
        className={cn(`text-[16px] text-center`, {
          "text-gray-300": machine.state === "OUT_OF_ORDER",
          "text-[#dc2626]": machine.state === "NOT_AVAILABLE" && isRunning,
          "text-[#9ca3af]": machine.state === "NOT_AVAILABLE" && !isRunning,
          "text-[#f97316]": machine.state === "RESERVED",
          "text-[#2563eb]":
            machine.state === "AVAILABLE" ||
            machine.state === "AVAILABLE_BY_RESERVATION",
        })}
      >
        {formattedState}
      </small>
      <small
        className={cn(
          `text-[16px] text-center mb-3`,
          !isAvailable(machine.state) && "text-gray-300",
        )}
      >
        {machine.capacity.toString()} kg
      </small>
      {isRunning && machineFinishWindow && (
        <small className="text-[14px] text-center mb-3 text-[#dc2626]">
          {t(finishWindowLabels[machineFinishWindow])}
        </small>
      )}
      {machine.state === "RESERVED" && (
        <small
          className={cn(
            `text-[14px] font-bold text-center mb-3 bg-[#FFF3CD] border border-[#FFE69C] text-[#856404] px-2 py-1 rounded max-w-max mx-auto`,
          )}
        >
          <Trans>Foglalt {{ reservedFor: machine.reservedFor }}-ra</Trans>
        </small>
      )}
      <p
        className={cn(
          `text-center font-bold text-[24px] font-helvetica inline-flex flex-col justify-end`,
          !isAvailable(machine.state) && "text-gray-300",
        )}
      >
        {nthPurchaseCampaignPercentage
          ? campaignOriginalPrice !== null && (
              <Strikethrough disabled={!isAvailable(machine.state)}>
                {formatCurrency(
                  campaignOriginalPrice,
                  shop.currencyCode ?? "HUF",
                )}
              </Strikethrough>
            )
          : coupon &&
            machine.canUseCoupon && (
              <Strikethrough disabled={!isAvailable(machine.state)}>
                {formatCurrency(
                  machine.originalPrice,
                  shop.currencyCode ?? "HUF",
                )}
              </Strikethrough>
            )}
        {formatCurrency(getMachinePrice(machine), shop.currencyCode ?? "HUF")}
      </p>
    </Card>
  );
}

function MachineCardSkeleton() {
  return (
    <Card className="px-2 pb-2 pt-0 gap-0 rounded-lg shadow-xl border-2 border-gray-200 min-w-[20vw]">
      <div className="flex justify-center gap-2 pb-2">
        <Skeleton className="rounded-b-2xl w-[58px] h-[52px]" />
      </div>
      <div className="min-h-8" />
      <Skeleton className="max-w-22 mx-auto w-full mb-1 h-[103px] rounded-md" />
      <Skeleton className="h-[20px] w-[80px] mx-auto mt-1 rounded" />
      <Skeleton className="h-[20px] w-[50px] mx-auto mt-1 mb-3 rounded" />
      <Skeleton className="h-[28px] w-[90px] mx-auto rounded" />
    </Card>
  );
}

// the washer's card keeps the usual blue: orange is the pet color outside the flow
const ACCENT_CARD = {
  dryer: {
    border: "border-bub-dryer",
    bg: "bg-bub-dryer",
    text: "text-bub-dryer",
    color: "#eb2d31",
  },
  pet: {
    border: "border-bub-pet",
    bg: "bg-bub-pet",
    text: "text-bub-pet",
    color: "#8e3fbf",
  },
} as const satisfies Record<
  Exclude<MachineAccent, "washer">,
  Record<string, string>
>;

/** Only free machines get colored; vegan ones stay green. */
function cardAccent(machine: Machine) {
  if (!isAvailable(machine.state) || machine.vegan) return null;
  const accent = machineAccent({
    kind: machine.machineType === "szaritogep" ? "dryer" : "washer",
    furFriendly: machine.furFriendly,
  });
  return accent === "washer" ? null : accent;
}

function isAvailable(status: string): boolean {
  return !(status !== "AVAILABLE_BY_RESERVATION" && status !== "AVAILABLE");
}

function getLegacyMachineIcon(machine: Pick<Machine, "machineType" | "state">) {
  // no dedicated legacy artwork for unavailable-but-not-running; the
  // out-of-order icon is the closest match
  const isTaken =
    machine.state === "RESERVED" ||
    (machine.state === "NOT_AVAILABLE" && isRunningReason(machine));
  return machine.machineType === "mosogep"
    ? machine.state === "AVAILABLE" ||
      machine.state === "AVAILABLE_BY_RESERVATION"
      ? `/washing-machine-available.svg`
      : isTaken
        ? `/washing-machine-reserved.svg`
        : `/washing-machine-out-of-order.svg`
    : machine.state === "AVAILABLE" ||
        machine.state === "AVAILABLE_BY_RESERVATION"
      ? `/drying-machine-available.svg`
      : isTaken
        ? `/drying-machine-reserved.svg`
        : `/drying-machine-out-of-order.svg`;
}

function TopBadge({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        `rounded-b-2xl p-[10px] text-[24px] font-bold text-white`,
        className,
      )}
    >
      <span className="min-w-[38px] inline-block text-center text-white">
        {children}
      </span>
    </div>
  );
}

function getMachinePrice(machine: Machine) {
  if (machine.state === "AVAILABLE_BY_RESERVATION") {
    return 0;
  }
  if (machine.machineType === "mosogep") {
    return machine.price;
  }
  if (!machine.dryerBundles?.length) {
    return machine.price ?? 0;
  }
  return formatPriceRange(
    machine.dryerBundles.map((bundle: { price: number }) => bundle.price),
  );
}

/** The price without the nth-purchase-campaign discount, in the same shape as
 * getMachinePrice, or null when it would not differ. Dryer bundles carry their
 * own originalPrice; plain machines have the base price as originalPrice. */
type PricedMachine = {
  price?: number | null;
  originalPrice?: number | null;
  dryerBundles?: { price: number; originalPrice?: number | null }[];
};

function getOriginalPrice(machine: Machine) {
  if (machine.state === "AVAILABLE_BY_RESERVATION") {
    return null;
  }
  const priced = machine as PricedMachine;
  const bundles =
    machine.machineType === "mosogep" ? [] : (priced.dryerBundles ?? []);
  const original = bundles.length
    ? formatPriceRange(
        bundles.map((bundle) => bundle.originalPrice || bundle.price),
      )
    : `${priced.originalPrice ?? priced.price ?? 0}`;
  return original === `${getMachinePrice(machine)}` ? null : original;
}

function formatPriceRange(prices: number[]) {
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);

  if (minPrice === maxPrice) {
    return `${minPrice}`;
  }
  return `${minPrice} - ${maxPrice}`;
}

export function Strikethrough({
  disabled,
  children,
  className,
}: {
  disabled?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        "text-sm leading-[1em] relative w-fit mx-auto",
        disabled && "text-gray-300",
        className,
      )}
    >
      <div
        className={cn(
          "absolute right-0 w-full top-[40%] skew-[-10deg] border-b-2 border-b-bub-dark-blue",
          disabled && "border-b-gray-300",
        )}
      />
      {children}
    </span>
  );
}
